import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Hash, Scale, Type, Calculator, Calendar, CheckCircle2, CreditCard, RotateCcw, Search, ShoppingCart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TEDDY_NAMES } from "./teddyNames";

const gameTypeConfig: Record<string, { label: string; icon: React.ElementType; placeholder: string }> = {
  number: { label: "Pick a Number", icon: Hash, placeholder: "" },
  weight: { label: "Guess the Weight", icon: Scale, placeholder: "e.g. 2.5kg" },
  name: { label: "Guess the Name", icon: Type, placeholder: "e.g. Teddy" },
  quantity: { label: "Guess the Quantity", icon: Calculator, placeholder: "e.g. 142" },
};

interface GamePlayViewProps {
  game: any;
  entries: any[]; // validation entries (number_picked, guess_value only)
  myEntries: any[]; // full entries for current parent
  ptaId: string;
  parentId: string | null;
  initialPlayerName: string;
  onBack: () => void;
  onEntrySubmitted: () => void;
  onGoToCart?: () => void;
}

export default function GamePlayView({
  game, entries, myEntries, ptaId, parentId, initialPlayerName, onBack, onEntrySubmitted, onGoToCart,
}: GamePlayViewProps) {
  const { toast } = useToast();
  const [playerName, setPlayerName] = useState(initialPlayerName);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [guessValue, setGuessValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingEntries, setPendingEntries] = useState<string[]>([]);
  const [showPostSubmit, setShowPostSubmit] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [nameSearch, setNameSearch] = useState("");

  const config = gameTypeConfig[game.game_type] || gameTypeConfig.number;
  const Icon = config.icon;
  const takenNumbers = entries.filter(e => e.number_picked != null).map(e => e.number_picked);
  const takenGuesses = entries.filter(e => e.guess_value != null).map(e => e.guess_value.toLowerCase());

  const isFreeGame = !game.entry_price || game.entry_price <= 0;

  // Filter names for teddy name picker
  const availableNames = useMemo(() => {
    return TEDDY_NAMES.filter(n => !takenGuesses.includes(n.toLowerCase()));
  }, [takenGuesses]);

  const filteredNames = useMemo(() => {
    if (!nameSearch) return availableNames;
    return availableNames.filter(n => n.toLowerCase().includes(nameSearch.toLowerCase()));
  }, [availableNames, nameSearch]);

  const handleNumberToggle = (num: number) => {
    if (takenNumbers.includes(num)) return;
    setSelectedNumbers(prev =>
      prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num]
    );
  };

  const handleNameToggle = (name: string) => {
    setSelectedNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const handleSubmit = async () => {
    if (!playerName.trim()) {
      toast({ title: "Name required", description: "Please enter your name.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const newEntryIds: string[] = [];
      const paymentStatus = isFreeGame ? "free" : "pending";

      if (game.game_type === "number") {
        if (selectedNumbers.length === 0) {
          toast({ title: "Select numbers", description: "Please select at least one number.", variant: "destructive" });
          setSubmitting(false);
          return;
        }
        for (const num of selectedNumbers) {
          const { data } = await supabase.from("guessing_game_entries").insert({
            game_id: game.id, pta_id: ptaId, player_name: playerName,
            number_picked: num, parent_id: parentId, payment_status: paymentStatus,
          }).select("id").single();
          if (data) newEntryIds.push(data.id);
        }
      } else if (game.game_type === "name") {
        if (selectedNames.length === 0) {
          toast({ title: "Select names", description: "Please select at least one name.", variant: "destructive" });
          setSubmitting(false);
          return;
        }
        for (const name of selectedNames) {
          const { data } = await supabase.from("guessing_game_entries").insert({
            game_id: game.id, pta_id: ptaId, player_name: playerName,
            guess_value: name, parent_id: parentId, payment_status: paymentStatus,
          }).select("id").single();
          if (data) newEntryIds.push(data.id);
        }
      } else {
        if (!guessValue.trim()) {
          toast({ title: "Enter guess", description: "Please enter your guess.", variant: "destructive" });
          setSubmitting(false);
          return;
        }
        // Duplicate validation for weight/quantity
        if (takenGuesses.includes(guessValue.trim().toLowerCase())) {
          toast({ title: "Already taken", description: "That guess has already been made. Please choose another.", variant: "destructive" });
          setSubmitting(false);
          return;
        }
        const { data } = await supabase.from("guessing_game_entries").insert({
          game_id: game.id, pta_id: ptaId, player_name: playerName,
          guess_value: guessValue, parent_id: parentId, payment_status: paymentStatus,
        }).select("id").single();
        if (data) newEntryIds.push(data.id);
      }

      setPendingEntries(prev => [...prev, ...newEntryIds]);
      onEntrySubmitted();

      setShowPostSubmit(true);
      setSelectedNumbers([]);
      setSelectedNames([]);
      setGuessValue("");
      setNameSearch("");
    } catch {
      toast({ title: "Error", description: "Failed to submit entry.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const handlePayNow = async () => {
    if (pendingEntries.length === 0) return;
    setPaymentLoading(true);
    try {
      const totalAmount = pendingEntries.length * (game.entry_price || 0);
      const { data, error } = await supabase.functions.invoke("create-game-entry-checkout", {
        body: {
          ptaId,
          entryIds: pendingEntries,
          amount: totalAmount,
          gameId: game.id,
          gameName: game.name,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else if (data?.error) {
        toast({ title: "Payment error", description: data.error, variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Payment error", description: err.message || "Failed to start payment", variant: "destructive" });
    }
    setPaymentLoading(false);
  };

  const handlePlayAgain = () => {
    setShowPostSubmit(false);
  };

  // Post-submit screen for both free and paid games
  if (showPostSubmit) {
    const totalEntries = pendingEntries.length;
    const totalCost = totalEntries * (game.entry_price || 0);

    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-6">
        <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Entry Added!</h2>

        {isFreeGame ? (
          <p className="text-muted-foreground">Your guess has been submitted. Good luck! 🎉</p>
        ) : (
          <p className="text-muted-foreground">
            Your {totalEntries === 1 ? "entry has" : "entries have"} been added to your cart.
            Head to the cart to complete payment.
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <Button onClick={handlePlayAgain} variant="outline" className="gap-2">
            <RotateCcw className="h-4 w-4" /> Play Again
          </Button>
          {!isFreeGame && onGoToCart && (
            <Button onClick={onGoToCart} className="gap-2">
              <ShoppingCart className="h-4 w-4" />
              Go to Cart
            </Button>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={onBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Games
        </Button>
      </div>
    );
  }

  const selectionCount = game.game_type === "number" ? selectedNumbers.length
    : game.game_type === "name" ? selectedNames.length
    : guessValue.trim() ? 1 : 0;
  const selectionCost = selectionCount * (game.entry_price || 0);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back to Games
      </Button>

      {/* Hero image */}
      {game.image_url && (
        <div className="rounded-xl overflow-hidden">
          <img src={game.image_url} alt={game.name} className="w-full h-auto rounded-xl" />
        </div>
      )}

      {/* Info */}
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">{game.name}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Icon className="h-3 w-3" /> {config.label}
          </Badge>
          {game.entry_price > 0 && (
            <Badge>£{game.entry_price.toFixed(2)} per entry</Badge>
          )}
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Ends {new Date(game.end_date).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* My previous entries */}
      {myEntries.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Your Entries</h3>
            <div className="flex flex-wrap gap-2">
              {myEntries.map((entry) => (
                <Badge key={entry.id} variant="secondary" className="text-xs">
                  {entry.number_picked != null ? `#${entry.number_picked}` : entry.guess_value}
                  {entry.payment_status === "pending" && " (unpaid)"}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Play area */}
      <Card>
        <CardContent className="p-6 space-y-5">
          {/* Player name */}
          <div className="space-y-2">
            <Label htmlFor="player-name">Your Name</Label>
            <Input
              id="player-name"
              placeholder="Enter your name"
              value={playerName}
              onChange={e => setPlayerName(e.target.value)}
            />
          </div>

          {/* Number grid */}
          {game.game_type === "number" && game.range_start != null && game.range_end != null && (
            <div className="space-y-2">
              <Label>Select your numbers</Label>
              <div className="grid grid-cols-10 gap-1.5 max-h-64 overflow-y-auto p-1">
                {Array.from({ length: game.range_end - game.range_start + 1 }, (_, i) => {
                  const num = game.range_start + i;
                  const taken = takenNumbers.includes(num);
                  const selected = selectedNumbers.includes(num);
                  return (
                    <button
                      key={num}
                      onClick={() => handleNumberToggle(num)}
                      disabled={taken}
                      className={`aspect-square flex items-center justify-center text-sm rounded-md border font-medium transition-all ${
                        taken
                          ? "bg-destructive/10 text-destructive/50 cursor-not-allowed border-destructive/20 line-through"
                          : selected
                          ? "bg-primary text-primary-foreground border-primary shadow-sm scale-105"
                          : "bg-card hover:bg-muted border-border hover:border-primary/40"
                      }`}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
              {selectedNumbers.length > 0 && (
                <p className="text-sm text-primary font-medium">
                  Selected: {selectedNumbers.sort((a, b) => a - b).join(", ")}
                </p>
              )}
            </div>
          )}

          {/* Name picker for teddy/name games — multi-select */}
          {game.game_type === "name" && (
            <div className="space-y-2">
              <Label>Choose Names (select as many as you like)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search names..."
                  value={nameSearch}
                  onChange={e => setNameSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-1.5 max-h-64 overflow-y-auto p-1">
                {filteredNames.map(name => {
                  const isSelected = selectedNames.includes(name);
                  return (
                    <button
                      key={name}
                      onClick={() => handleNameToggle(name)}
                      className={`px-2 py-1.5 text-xs rounded-md border font-medium transition-all truncate ${
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-card hover:bg-muted border-border hover:border-primary/40"
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
                {filteredNames.length === 0 && (
                  <p className="col-span-full text-sm text-muted-foreground text-center py-4">
                    No matching names available
                  </p>
                )}
              </div>
              {selectedNames.length > 0 && (
                <p className="text-sm text-primary font-medium">
                  Selected ({selectedNames.length}): {selectedNames.join(", ")}
                </p>
              )}
            </div>
          )}

          {/* Guess input for weight/quantity */}
          {game.game_type !== "number" && game.game_type !== "name" && (
            <div className="space-y-2">
              <Label htmlFor="guess">Your Guess</Label>
              <Input
                id="guess"
                placeholder={config.placeholder}
                value={guessValue}
                onChange={e => setGuessValue(e.target.value)}
              />
            </div>
          )}

          {/* Selection summary */}
          {selectionCount > 0 && !isFreeGame && (
            <div className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
              {selectionCount} {selectionCount === 1 ? "entry" : "entries"} × £{(game.entry_price || 0).toFixed(2)} = <span className="font-semibold text-foreground">£{selectionCost.toFixed(2)}</span>
            </div>
          )}

          <Button
            className="w-full"
            size="lg"
            onClick={handleSubmit}
            disabled={submitting || !playerName.trim()}
          >
            {submitting ? "Submitting..." : selectionCount > 0
              ? `Add ${selectionCount} ${selectionCount === 1 ? "Entry" : "Entries"} to Cart`
              : "Submit Entry"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
